<template>
  <v-col class="py-0">
    <v-sheet
      class="pa-7"
      v-bind:style="{ position: 'relative' }"
    >

      <v-input
        :rules="rules"
        :value="inputData ? inputData.value : null"
      >
        <label class="v-label theme--light">{{ label }}</label>

      </v-input>

      <sheet-loader :loading="loading" />

      <v-toolbar flat>
        <v-btn
          fab
          text
          color="grey darken-2"
          @click="prev">
          <v-icon> mdi-chevron-left </v-icon>
        </v-btn>

        <v-spacer></v-spacer>

        <v-toolbar-title v-if="title">
          {{ title }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn fab text right color="grey darken-2" @click="next">
          <v-icon> mdi-chevron-right </v-icon>
        </v-btn>
        <v-btn
          v-if="type === 'day'"
          right
          color="primary"
          @click="viewMonth"
        >
          Ver mes
        </v-btn>
      </v-toolbar>

      <v-calendar
        ref="calendar"
        color="primary"
        v-model="calendarDate"
        :type="type"
        :locale="$i18n.locale"
        :weekdays="weekdays"
        :first-time= startTime
        :interval-count= intervals
        category-show-all
        interval-height= '75px'
        :events="type === 'day' ? calendarEvents : []"
        :event-color="getEventColor"
        event-overlap-mode="stack"
        event-overlap-threshold="60"
        @click:date="viewDay"
        @click:event="selectAppointment($event)"
        @change="updateCalendarEvents"
        v-bind:style="{ minHeight:'200px' }"
      >
        <template v-slot:day="{ date }">
          <v-row no-gutters class="fill-height time-availability">
            <template v-if="events[date]">
              <v-sheet
                width="100%"
                height="100%"
                tile
                :color="getAvailabilityColor(date)"
              ></v-sheet>
            </template>
          </v-row>
        </template>

        <template v-slot:event="{ event }" v-if="type === 'day'" >
           <strong class="ml-2">{{event.name}}</strong>
        </template>

        <template v-slot:day-label-header="{ day, present }">
          <span v-bind:class="{ 'primary--text': present, 'disabled': disabled }">
            {{ day }}
          </span>
        </template>
      </v-calendar>

      <div v-if="inputDataDate != null" class="ma-2">
          {{ 'Cita reservada el ' }} {{ inputDataDate | datetime }}
      </div>

      <!-- event detail -->
      <v-menu
        v-model="selectedOpen"
        :close-on-content-click="false"
        :activator="selectedElement"
        offset-x
      >
        <v-card color="grey lighten-4" min-width="350px" flat>
          <v-toolbar color="primary" dense dark>
            <v-btn icon>
              <v-icon>mdi-clock-outline</v-icon>
            </v-btn>
            <v-toolbar-title class="pl-0 body-1">{{ selectedEvent.name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              @click="selectAppointment()"
              depressed
              color="white"
              class="ml-4"
              light
              small>
              reservar
            </v-btn>
          </v-toolbar>
        </v-card>
      </v-menu>
    </v-sheet>
  </v-col>
</template>

<script>
import moment from 'moment';
import { SheetLoader } from '@/components';

const AVAILABLE_DATES_QUERY = 'acude_dates';
const AVAILABLE_SCHEDULE_QUERY = 'acude_schedules';

const timeToDecimal = (time) => {
  const splitted = time.split(':');
  const decimals = parseInt((splitted[1] / 6) * 10, 10);
  return parseFloat(parseInt(splitted[0], 10) + (decimals / 100));
};

const search = (client) => async (resource, filters) => {
  try {
    return await client.search({
      target: 'namedquery',
      entity: resource,
      filters,
    });
  } catch (ex) {
    console.log('search request error', ex);
    throw ex;
  }
};

export default {
  props: {
    label: String,
    hint: String,
    disabled: Boolean,
    initialValue: {
      required: false,
    },
    inputData: Object,
    rules: {
      type: Array,
      default: () => [],
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      default: () => false,
    },
    /* ******************************** */
    weekdays: {
      type: Array,
      default: () => [1, 2, 3, 4, 5, 6, 0],
    },
  },
  components: { SheetLoader },
  data({ params }) {
    const getParam = (param, defaultValue) => {
      if (typeof params[param] !== 'undefined') {
        return params[param];
      }
      return defaultValue || null;
    };

    return {
      client: null,
      searchArguments: null, // auto update data handler
      type: 'month',
      calendarDate: moment().format('YYYY-MM-DD'),
      calendarEvents: [],
      events: {},
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      title: null,
      loading: false,
      skipNextSearch: false,
      currentAgenda: null,
      intervals: 12,
      startTime: getParam('starttime', '09:00'), // hours
      endTime: getParam('starttime', '21:00'), // hours
      slotDuration: parseInt(getParam('slotduration', '30'), 10), // minutes
    };
  },
  updated() {
    if (this.type === 'day') {
      const presentDayHeader = this.$el.querySelector('.v-present');
      if (presentDayHeader) {
        presentDayHeader.className += ' primary';
        presentDayHeader.querySelectorAll('.primary--text').forEach(($node) => {
          // eslint-disable-next-line no-param-reassign
          $node.className = $node.className.replace('primary--text', 'white--text');
        });
      }
    }
  },
  methods: {
    // retrieve available days
    async getAgendaDates(startDate, endDate) {
      this.loading = true;

      const [group, agenda] = ['group', 'agenda'].map((key) => (this.params[key] ? this.evalParam(this.params[key]) : undefined));
      if (agenda == null && group == null) {
        throw new Error('cannot retrieve agenda dates: got group and agenda with null values');
      }

      this.searchArguments = { group, agenda };

      try {
        const result = await this.search(this.availableDatesQuery, {
          startDate,
          endDate,
          agenda,
          group,
        });

        if (result.total) {
          result.list
            .map((dateRow) => ({ date: moment(dateRow.date).format('YYYY-MM-DD') }))
            .forEach(({ date }) => {
              if (!this.events[date]) {
                this.events[date] = [];
              }
            });

          const firstRow = result.list[0];
          this.currentAgenda = firstRow.agenda;
        }
      } catch (ex) {
        console.error('error getting agenda available dates', ex);
      }

      this.loading = false;
    },
    async getDateSchedule(date) {
      if (this.events[date] && !this.events[date].length) {
        this.loading = true;

        const [group, agenda] = ['group', 'agenda'].map((key) => (this.params[key] ? this.evalParam(this.params[key]) : undefined));
        if (agenda == null && group == null && this.currentAgenda == null) {
          throw new Error('cannot retrieve agenda date schedules: got group and agenda with null values');
        }

        try {
          const result = await this.search(this.availableScheduleQuery, {
            date,
            group,
            agenda: this.currentAgenda || agenda,
          });
          if (result.total) {
            const agendaSchedule = result.unique();
            this.events[date] = agendaSchedule.schedules;
            this.slotDuration = agendaSchedule.duration;

            const dayStartTime = this.events[date][0];
            const dayEndTime = this.events[date][this.events[date].length - 1];
            console.log('starTime:', timeToDecimal(dayStartTime), timeToDecimal(dayStartTime) < timeToDecimal(this.startTime), timeToDecimal(this.startTime));

            if (timeToDecimal(dayStartTime) > timeToDecimal(this.startTime)) {
              // eslint-disable-next-line prefer-destructuring
              this.startTime = `${Number(dayStartTime.substring(0, 2)) - 1}:${dayStartTime.substring(2)}`;
            }
            console.log('endTime:', timeToDecimal(dayEndTime), timeToDecimal(dayEndTime) > timeToDecimal(this.endTime), timeToDecimal(this.endTime));
            if (timeToDecimal(dayEndTime) < timeToDecimal(this.endTime)) {
              this.endTime = `${Number(dayEndTime.substring(0, 2)) + 1}:${dayEndTime.substring(2)}`;
            }
            this.intervals = (this.events[date].length / (60 / this.slotDuration)) + 2;
          }
        } catch (ex) {
          console.error('error getting date schedules', ex);
        }

        this.loading = false;
      }
      this.calendarEvents = (this.events[date] || []).map((time) => this.buildEvent(date, time));
    },
    buildEvent(dateStr, time) {
      const date = moment(dateStr);
      const startEvt = moment(`${date.format('YYYY-MM-DD')} ${time}`);
      return {
        name: `Cita ${this.isSelectedDate(startEvt) ? 'reservada' : 'disponible'} a las ${time}`,
        start: startEvt.format('YYYY-MM-DD HH:mm'),
        end: startEvt.add(this.slotDuration, 'minutes').format('YYYY-MM-DD HH:mm'),
        timed: false,
      };
    },
    evalParam(param, v) {
      const str = typeof param === 'function' ? param({ value: v }) : param;
      try {
        return JSON.parse(str || '{}');
      } catch (ex) {
        return str;
      }
    },
    // perform action if component is not locked or disabled
    performIf(action) {
      if (!this.loading && !this.disabled) {
        action();
      }
    },
    next() {
      this.performIf(() => this.$refs.calendar.next());
    },
    prev() {
      this.performIf(() => this.$refs.calendar.prev());
    },
    viewDay({ date }) {
      this.performIf(() => {
        this.calendarDate = date;
        this.type = 'day';
      });
    },
    viewMonth() {
      this.performIf(() => {
        this.skipNextSearch = true;
        this.type = 'month';
      });
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        setTimeout(() => {
          this.selectedOpen = true;
        }, 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    updateCalendarEvents({ start, end }) {
      if (this.$refs.calendar) {
        this.title = this.$refs.calendar.title;
      }

      if (this.skipNextSearch) {
        this.skipNextSearch = false;
        return;
      }

      if (this.type === 'month') {
        this.getAgendaDates(
          moment(start.date).clone().startOf('month').toISOString(),
          moment(end.date).clone().endOf('month').toISOString(),
        );
      } else {
        this.getDateSchedule(start.date);
      }
    },
    selectAppointment({ event, day }) {
      this.$emit('update:inputData', {
        value: {
          agenda: this.currentAgenda,
          date: moment(event.start).toISOString(),
        },
      });
      this.selectedEvent = event;
      this.selectedOpen = false;
      this.viewDay(day);
    },
    isSelectedDate(date, comparisonReference) {
      let selectedDate = false;
      try {
        if (this.inputDataDate != null) {
          selectedDate = moment(date).isSame(moment(this.inputDataDate), comparisonReference);
        }
      } catch (ex) { /* ignored exception */ }
      return selectedDate;
    },
    getAvailabilityColor(date) {
      return this.isSelectedDate(date, 'day') ? 'primary' : 'green';
    },
    getEventColor({ start }) {
      return this.isSelectedDate(start) ? 'primary lighten-1' : 'light-green lighten-1';
    },
    async resetCalendar() {
      this.selectedEvent = {};
      this.selectedElement = null;
      this.selectedOpen = false;
      this.events = {};
      this.calendarEvents = [];
      this.currentAgenda = null;
      this.viewMonth();
      const now = moment();
      this.calendarDate = now.format('YYYY-MM-DD');
      await this.getAgendaDates(
        now.clone().startOf('month').toISOString(),
        now.clone().endOf('month').toISOString(),
      );
      this.skipNextSearch = false;
    },
    getElement(e) {
      console.log('Element', e);
      return e.name;
    },
  },
  async created() {
    this.search = search(this.$api.getClient('search'));

    this.availableDatesQuery = AVAILABLE_DATES_QUERY;
    if (typeof this.params.availabledatesquery === 'string') {
      this.availableDatesQuery = this.params.availabledatesquery;
    }

    this.availableScheduleQuery = AVAILABLE_SCHEDULE_QUERY;
    if (typeof this.params.availableschedulequery === 'string') {
      this.availableScheduleQuery = this.params.availableschedulequery;
    }
  },
  computed: {
    globalContext() {
      return this.$parent.expressionInterpreter.ctx;
    },
    inputDataDate() {
      if (this.inputData == null || this.inputData.value == null) {
        return null;
      }
      return this.inputData.value.date;
    },
  },
  watch: {
    globalContext(val) {
      const shouldUpdateComponent = ['group', 'agenda']
        .filter((key) => this.params[key])
        .map((key) => [key, this.evalParam(this.params[key])])
        .some((neweval) => {
          const newchanges = JSON.stringify(neweval[1]);
          const oldchanges = JSON.stringify(this.searchArguments[neweval[0]]);
          // console.log('old', oldchanges, 'vs new', newchanges);
          return newchanges !== oldchanges;
        });

      if (shouldUpdateComponent) {
        console.log(`updating ${ this.$parent.alias } component...`);
        this.$emit('update:inputData', { value: undefined });
        this.resetCalendar();
      }

      return val;
    },
  },
};
</script>

<style lang="scss">
$time-availability-height: 5px;

.v-calendar-weekly__day-label {
  position: relative;
}

// FIX internet explorer acude calendar display
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .v-calendar-monthly {
    display: table !important;
  }

  .v-calendar-daily {
    display: list-item !important;
  }
}

.time-availability {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: $time-availability-height;
}

@media screen and (max-width: 960px) {
  .v-calendar .v-event-timed-container {
    margin-right: 0!important;
  }
}
</style>
